import { useState, type CSSProperties } from 'react';

import { cn } from '~/utils/cn';
import { getBankLogo, hasBankLogo, type InstitutionLike } from '~/utils/bankLogo';

import { Text } from '../Text/view';

export type BankAvatarSize = 'xs' | 'sm' | 'md' | 'lg' | 'xl';

interface PropsBankAvatar {
  institution?: InstitutionLike;
  size?: BankAvatarSize;
  className?: string;
  style?: CSSProperties;
  ring?: boolean;
}

const SIZE_MAP: Record<
  BankAvatarSize,
  { box: number; radius: number; padding: number; textClass: string }
> = {
  xs: { box: 28, radius: 8, padding: 3, textClass: 'text-caption' },
  sm: { box: 32, radius: 10, padding: 4, textClass: 'text-captionLG' },
  md: { box: 40, radius: 12, padding: 4, textClass: 'text-paragraphMD' },
  lg: { box: 56, radius: 16, padding: 6, textClass: 'text-paragraphLG' },
  xl: { box: 72, radius: 20, padding: 8, textClass: 'text-headingH2' },
};

const computeInitials = (name?: string, shortCode?: string): string => {
  if (shortCode) return shortCode.slice(0, 2).toUpperCase();
  if (!name) return '—';
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '—';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
};

export default function BankAvatar({
  institution,
  size = 'md',
  className,
  style,
  ring = true,
}: PropsBankAvatar) {
  const sizing = SIZE_MAP[size];
  const knownBank = hasBankLogo(institution);
  const [imgFailed, setImgFailed] = useState(false);
  const showImage = knownBank && !imgFailed;
  const logo = getBankLogo(institution);
  const initials = computeInitials(institution?.name, institution?.shortCode);

  return (
    <div
      className={cn(
        'flex items-center justify-center overflow-hidden shrink-0 bg-intense-01',
        ring && 'border border-intense-03',
        className,
      )}
      style={{
        width: sizing.box,
        height: sizing.box,
        borderRadius: sizing.radius,
        padding: showImage ? sizing.padding : 0,
        ...style,
      }}
    >
      {showImage ? (
        <img
          src={logo}
          alt={institution?.name ?? 'Banco'}
          className="w-full h-full object-contain"
          onError={() => setImgFailed(true)}
        />
      ) : (
        <div
          className="w-full h-full flex items-center justify-center bg-support-01"
          style={{ borderRadius: sizing.radius }}
        >
          <Text
            className={cn(sizing.textClass, 'color-primary-01 tracking-wide')}
          >
            {initials}
          </Text>
        </div>
      )}
    </div>
  );
}
